import React from "react";
import { Box, Tooltip } from "@mui/material";
import { ChevronRightRounded } from "@mui-symbols-material/w300";
import { InstalledBasePathNode } from "../../utils/installedBasePath";

interface Props {
    /** Root → leaf, already without the node itself (see getPathNodesAbove). */
    pathNodes: InstalledBasePathNode[];
    children: React.ReactElement;
}

// Off-screen but still part of the selection, so a copied path reads "A / B / C".
const visuallyHiddenSx = {
    position: "absolute",
    width: "1px",
    height: "1px",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
};

// Hover tooltip with the full installed base path, separated by chevrons. The
// tooltip stays open while hovered so the path can be selected and copied.
const InstalledBasePathTooltip = ({ pathNodes, children }: Props) => {
    const titles = pathNodes.map((node) => node.title ?? "").filter((title) => title !== "");
    if (!titles.length) return children;

    return (
        <Tooltip
            placement="top-start"
            enterDelay={700}
            enterNextDelay={500}
            slotProps={{ tooltip: { sx: { maxWidth: "none" } } }}
            title={
                <Box component="span" sx={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", userSelect: "text" }}>
                    {titles.map((title, index) => (
                        <React.Fragment key={index}>
                            {index > 0 && (
                                <>
                                    <ChevronRightRounded aria-hidden sx={{ fontSize: 16, mx: "2px" }} />
                                    <Box component="span" sx={visuallyHiddenSx}> / </Box>
                                </>
                            )}
                            <span>{title}</span>
                        </React.Fragment>
                    ))}
                </Box>
            }
        >
            {children}
        </Tooltip>
    );
};

export default InstalledBasePathTooltip;
